import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  FormSection,
  Button,
  Input,
  LoadingSpinner,
  SettingsSaveBar,
  useDirtyForm,
  useToastActions
} from '@airclou/ui'
import { Mail, Phone } from 'lucide-react'
import { dashboardApiClient } from '../../../services/api'
import { ErrorState } from '../../../components/state/ErrorState'

interface RecoverySettings {
  backup_email: string | null
  recovery_phone: string | null
  backup_email_verified: boolean
  recovery_phone_verified: boolean
}

export function RecoveryTab() {
  const { t } = useTranslation('dashboard')
  const { error: toastError } = useToastActions()
  const [settings, setSettings] = useState<RecoverySettings | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const form = useDirtyForm({ backup_email: '', recovery_phone: '' })
  const values = form.values

  const [codes, setCodes] = useState<string[] | null>(null)
  const [isGenerating, setIsGenerating] = useState(false)

  const load = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const res = await dashboardApiClient.getRecoverySettings()
      if (res) {
        setSettings(res)
        form.setBaseline({
          backup_email: res.backup_email || '',
          recovery_phone: res.recovery_phone || ''
        })
      }
    } catch (e: any) {
      setError(e.message || t('security.failedToLoadRecovery'))
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => { load() }, [])

  const handleSave = async () => {
    setSaving(true)
    try {
      await dashboardApiClient.updateRecoverySettings(values)
      await load()
    } catch (e: any) {
      // A save that fails must say so. This used to swallow the error, which
      // left the save bar up with no explanation — indistinguishable from a
      // slow network to the operator.
      toastError(e?.message || t('security.failedToSaveRecovery'), t('common.error'))
    } finally {
      setSaving(false)
    }
  }

  const handleGenerate = async () => {
    setIsGenerating(true)
    try {
      const res = await dashboardApiClient.generateBackupCodes()
      if (res) setCodes(res.codes)
    } catch {}
    finally { setIsGenerating(false) }
  }

  return (
    <div className="space-y-6">
      <FormSection
        title={t('security.accountRecovery')}
      >
        {isLoading ? (
          <div className="flex justify-center py-8"><LoadingSpinner /></div>
        ) : error ? (
          <ErrorState
            title={t('security.failedToLoadRecovery')}
            description={error}
            onRetry={load}
          />
        ) : settings && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="flex text-sm font-medium mb-1 items-center gap-2"><Mail className="h-4 w-4"/> {t('security.backupEmail')}</label>
                <Input
                  value={values.backup_email}
                  onChange={(value) => form.setField('backup_email', value)}
                  placeholder="name.backup@example.com"
                />
                {settings.backup_email_verified ? (
                  <p className="text-xs text-green-600 mt-1">{t('security.verified')}</p>
                ) : (
                  <p className="text-xs text-gray-500 mt-1">{t('security.backupEmailDesc')}</p>
                )}
              </div>
              <div>
                <label className="flex text-sm font-medium mb-1 items-center gap-2"><Phone className="h-4 w-4"/> {t('security.recoveryPhone')}</label>
                <Input
                  value={values.recovery_phone}
                  onChange={(value) => form.setField('recovery_phone', value)}
                  placeholder="+1 555 000 1234"
                />
                {settings.recovery_phone_verified ? (
                  <p className="text-xs text-green-600 mt-1">{t('security.verified')}</p>
                ) : (
                  <p className="text-xs text-gray-500 mt-1">{t('security.recoveryPhoneDesc')}</p>
                )}
              </div>
            </div>
          </div>
        )}
      </FormSection>

      <FormSection
        title={t('security.backupCodes')}
      >
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">{t('security.backupCodesDesc')}</p>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={handleGenerate} disabled={isGenerating}>
              {isGenerating ? t('security.generatingCodes') : t('security.generateNewCodes')}
            </Button>
          </div>
          {codes && (
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {codes.map((c, idx) => (
                <div key={idx} className="font-mono text-sm border border-border rounded px-3 py-2 bg-muted/50">{c}</div>
              ))}
            </div>
          )}
        </div>
      </FormSection>

      <SettingsSaveBar
        visible={form.isDirty}
        onSave={handleSave}
        onDiscard={form.reset}
        isSaving={saving}
        message={t('common.unsavedChanges')}
        saveLabel={t('security.saveChanges')}
        savingLabel={t('security.savingChanges')}
        discardLabel={t('common.discard')}
      />
    </div>
  )
}
